import ApiClient from "@/api/ApiClient.ts";
import { cache } from "@/api/cache";
import qs from "qs";

const STREETS_API_PATH = "streets";

/**
 * Get a street by id and workgroup that defaults to the workgroup from vuex
 *
 * @param {int} streetId the street id
 * @param {int} workgroupId the workgroup id, default to vuex workgroup
 */
export function getStreetById(streetId, workgroupId) {
  const params = {
    streetId: streetId,
    wgId: workgroupId,
  };
  return ApiClient()
    .get(STREETS_API_PATH, { params })
    .then((response) => response.data);
}

/**
 * Get streets by workgroup
 */
export async function getStreetsByWorkgroup(wgId) {
  try {
    const cacheKey = `streets-${wgId}`;
    const cachedStreets = await cache.get(cacheKey);
    if (cachedStreets) {
      return cachedStreets;
    }

    const streets = await ApiClient()
      .get(`${STREETS_API_PATH}/streetView`, { params: { wgId } })
      .then((response) => response.data);

    await cache.set(cacheKey, streets, 30 * 60 * 1000); // Cache for 30 minutes
    return streets;
  } catch (e) {
    console.error("An error ocurred loading streets");
    throw e;
  }
}

/**
 * Get streets for street input by workgroup
 */
export async function getStreetsForStreetInput(wgId, invalidateCache = false) {
  try {
    const cacheKey = `streets-for-input-${wgId}`;
    const cachedStreets = await cache.get(cacheKey);
    if (cachedStreets && !invalidateCache) {
      return cachedStreets;
    }

    const streets = await ApiClient()
      .get(`${STREETS_API_PATH}`, { params: { wgId, viewMode: "streetInput" } })
      .then((response) => response.data)
      .then((streets) => {
        streets.forEach((street) => {
          street.display = `${street.name}${street.hint ? ` (${street.hint})` : ""}`;
        });
        return streets;
      });

    await cache.set(cacheKey, streets, 30 * 60 * 1000); // Cache for 30 minutes
    return streets;
  } catch (e) {
    console.error("An error ocurred loading streets with streetInput view mode");
    throw e;
  }
}

/**
 * Get streets by central
 *
 * @param {int} wgId the workgroupId
 * @param {int} centralId the centralId
 * @returns the list of streets
 */
export async function getStreetsByCentral(wgId, centralId) {
  try {
    const params = {
      wgId,
      centralId,
    };
    return await ApiClient()
      .get(`${STREETS_API_PATH}/streetView`, { params })
      .then((response) => response.data);
  } catch (e) {
    console.error("An error ocurred loading streets");
    throw e;
  }
}

/**
 * Get streets with portals by central
 *
 * @param {int} wgId the workgroupId
 * @param {int} centralId the centralId
 * @returns the list of streets
 */
export async function getStreetsWithPortalsByCentral(wgId, centralId) {
  try {
    const params = {
      wgId,
      centralId,
      viewMode: "streetWindow",
    };
    return await ApiClient()
      .get(STREETS_API_PATH, { params })
      .then((response) => response.data);
  } catch (e) {
    console.error("An error ocurred loading streets");
    throw e;
  }
}

export async function getStreetsForDisableElizaOutOfUrbanArea(wgId, centralId) {
  try {
    const params = {
      wgId,
      centralId,
      viewMode: "disableElizaOutOfUrbanAreas",
    };
    return await ApiClient()
      .get(`${STREETS_API_PATH}/streetView`, { params })
      .then((response) => response.data);
  } catch (e) {
    console.error("An error ocurred loading streets");
    throw e;
  }
}

/**
 * Get street with all fields by id
 *
 * @param {int} wgId the workgroupId
 * @param {int} streetId the streetId
 * @returns the list of streets
 */
export async function getStreetByCentral(wgId, streetId) {
  try {
    const params = {
      wgId,
      streetId,
    };
    return await ApiClient()
      .get(STREETS_API_PATH, { params })
      .then((response) => response.data);
  } catch (e) {
    console.error("An error ocurred loading streets");
    throw e;
  }
}

/**
 * Update given street
 *
 * @param {int} wgId the workgroup id
 * @param {int} streetId the street id
 * @param {object} street the street data object
 */
export async function updateStreet(wgId, streetId, street) {
  const params = {
    wgId,
    streetId,
  };
  try {
    const urlFormData = qs.stringify({ values: JSON.stringify(street) });
    return await ApiClient().put(`${STREETS_API_PATH}`, urlFormData, {
      params,
      headers: { "content-type": "application/x-www-form-urlencoded" },
    });
  } catch (e) {
    console.error("An error ocurred updating street");
    throw e;
  }
}

/**
 * Create street in given central
 *
 * @param {int} wgId the workgroup id
 * @param {int} centralId the central id
 * @param {object} street the street data object
 */
export async function createStreet(wgId, centralId, street) {
  const params = {
    wgId,
    centralId,
  };
  try {
    const urlFormData = qs.stringify({ values: JSON.stringify(street) });
    return await ApiClient().post(`${STREETS_API_PATH}`, urlFormData, {
      params,
      headers: { "content-type": "application/x-www-form-urlencoded" },
    });
  } catch (e) {
    console.error("An error ocurred creating street");
    throw e;
  }
}

/**
 * Delete given street
 *
 * @param {int} wgId the workgroup id
 * @param {int} streetId the street id
 */
export async function deleteStreetById(wgId, streetId) {
  try {
    const params = {
      wgId,
      streetId,
    };
    return await ApiClient()
      .delete(`${STREETS_API_PATH}`, { params })
      .then((response) => response.data);
  } catch (e) {
    console.error("An error ocurred deleting street");
    throw e;
  }
}

export async function updateStreetPortal(wgId, streetId, portalId, portal) {
  const params = {
    wgId,
    streetId,
    portalId,
  };
  try {
    const urlFormData = qs.stringify({ values: JSON.stringify(portal) });
    return await ApiClient().put(`${STREETS_API_PATH}/portal`, urlFormData, {
      params,
      headers: { "content-type": "application/x-www-form-urlencoded" },
    });
  } catch (e) {
    console.error("An error ocurred updating portal");
    throw e;
  }
}

export async function getPortalCalculatedData(wgId, streetId, coords) {
  const params = {
    wgId,
  };
  try {
    const body = {
      streetId,
      coords,
    };
    return (await ApiClient().post(`${STREETS_API_PATH}/portalCalculatedData`, body, { params }))
      .data;
  } catch (e) {
    console.error(e);
    throw e;
  }
}

export async function createStreetPortal(wgId, centralId, streetId, portal) {
  const params = {
    wgId,
    centralId,
    streetId,
  };
  try {
    const urlFormData = qs.stringify({ values: JSON.stringify(portal) });
    return await ApiClient().post(`${STREETS_API_PATH}/portal`, urlFormData, {
      params,
      headers: { "content-type": "application/x-www-form-urlencoded" },
    });
  } catch (e) {
    console.error("An error ocurred creating portal");
    throw e;
  }
}

export async function deleteStreetPortalById(wgId, streetId, portalIds) {
  try {
    const params = {
      wgId,
      streetId,
      portalId: JSON.stringify(portalIds),
    };
    return await ApiClient()
      .delete(`${STREETS_API_PATH}/portal`, { params })
      .then((response) => response.data);
  } catch (e) {
    console.error("An error ocurred deleting portals");
    throw e;
  }
}

export async function getStreetsWithoutPortals(idWg) {
  return await ApiClient()
    .get(`${STREETS_API_PATH}/streetsWithoutPortal`, { params: { idWg } })
    .then((response) => response.data);
}

export async function getPortalsWithoutDelivery(idWg) {
  return await ApiClient()
    .get(`${STREETS_API_PATH}/portalsWithoutDelivery`, { params: { idWg } })
    .then((response) => response.data)
    .then((data) => data.filter((i) => typeof i === "object"));
}

export async function getAllPortals(idWg) {
  return await ApiClient()
    .get(`${STREETS_API_PATH}/discretePortals`, { params: { idWg } })
    .then((response) => response.data);
}

export async function getPostalCodePolygons() {
  return await ApiClient()
    .get(`${STREETS_API_PATH}/postalCodePolygons`)
    .then((response) => response.data);
}

export async function getCNIGCartoForPostalCodes(postalCodes, idWg) {
  return await ApiClient()
    .get(`${STREETS_API_PATH}/cartoKbo?cp=${JSON.stringify(postalCodes)}&source=cnig&idWg=${idWg}`)
    .then((response) => response.data);
}

/**
 * Search streets using fuzzy search
 * @param {string} searchTerm - The term to search for
 * @param {Array} streets - Array of streets to search through
 * @param {Object} options - Search options
 * @returns {Array} Array of search results with scores and highlights
 */
export async function searchStreets(searchTerm, streets, options = {}) {
  try {
    if (!streets || streets.length === 0) {
      return [];
    }

    // Prepare streets for faster searching if they haven't been prepared yet
    const preparedStreets = prepareItems(streets, ["name", "address"]);

    // Perform fuzzy search
    const searchOptions = {
      keys: ["name", "address"],
      limit: options.limit || 10,
      threshold: options.threshold || -10000,
      ...options,
    };

    const results = fuzzySearch(searchTerm, preparedStreets, searchOptions);

    // Format results to include highlights
    return results.map((result) => ({
      ...result.obj,
      score: result.score,
      highlights: {
        name: result[0]?.highlight(),
        address: result[1]?.highlight(),
      },
    }));
  } catch (e) {
    console.error("An error occurred searching streets:", e);
    throw e;
  }
}
